import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";

/*
 * Reutiliza exatamente o padrão visual da tela Proprietários.
 * Não alterar Proprietarios.css para esta implementação.
 */
import "./Proprietarios.css";
import { limparCnpj, mascararCnpj } from "../utils/documento";
import { descreverDocumentoEstrangeiro } from "../utils/documentoEstrangeiro";

// ============================================================
// FORMULÁRIO VAZIO
// ============================================================

const FORM_VAZIO = {
  proprietarioId: null,
  proprietarioNome: "",
  cnpj: "",
  cep: "",
  endereco: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
  email: "",
  telefone: "",
};

// ============================================================
// FUNÇÕES AUXILIARES
// ============================================================

function apenasDigitos(valor) {
  return String(valor ?? "").replace(/\D/g, "");
}

function normalizarTexto(valor) {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function mascararCPF(valor) {
  return String(valor ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2")
    .substring(0, 14);
}

// CNPJ numérico ou alfanumérico (letras A-Z nas 12 primeiras posições).
function mascararCNPJ(valor) {
  return mascararCnpj(valor);
}

// CNPJ do proprietário ou, se estrangeiro sem CNPJ, o documento estrangeiro.
function cnpjOuDocumentoEstrangeiro(dados) {
  return dados?.cnpj ? mascararCNPJ(dados.cnpj) : descreverDocumentoEstrangeiro(dados);
}

function mascararCEP(valor) {
  return String(valor ?? "")
    .replace(/\D/g, "")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .substring(0, 9);
}

function mascararTelefone(valor) {
  const numeros = apenasDigitos(valor).substring(0, 11);

  if (!numeros) {
    return "";
  }

  if (numeros.length <= 2) {
    return `(${numeros}`;
  }

  if (numeros.length <= 6) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
  }

  if (numeros.length <= 10) {
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
  }

  return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`;
}

// ============================================================
// DOCUMENTO A4
// ============================================================

function montarEnderecoCompletoDocumento(dados) {
  const enderecoNumero = [dados?.endereco, dados?.numero].filter(Boolean).join(", ");

  const cidadeUf = [dados?.cidade, dados?.uf].filter(Boolean).join("/");

  return [enderecoNumero, dados?.complemento, dados?.bairro, cidadeUf, dados?.cep ? `CEP ${mascararCEP(dados.cep)}` : ""].filter(Boolean).join(" - ");
}

function montarRodapeDocumento(dados) {
  return [
    dados?.proprietarioNome || "",
    cnpjOuDocumentoEstrangeiro(dados),
    montarEnderecoCompletoDocumento(dados),
    dados?.email || "",
    dados?.telefone ? mascararTelefone(dados.telefone) : "",
  ]
    .filter(Boolean)
    .join(" | ");
}

function escaparHtml(valor) {
  return String(valor ?? "").replace(/[&<>"']/g, (caractere) => {
    const mapa = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };

    return mapa[caractere];
  });
}

function obterTipoProprietario(proprietario) {
  const tipo = normalizarTexto(proprietario?.tipoPessoa);

  if (tipo.includes("jurid") || limparCnpj(proprietario?.cnpj).length === 14) {
    return "PJ";
  }

  if (tipo.includes("fisic") || apenasDigitos(proprietario?.cpf).length === 11) {
    return "PF";
  }

  const documento = apenasDigitos(proprietario?.documento);

  if (documento.length === 14) {
    return "PJ";
  }

  if (documento.length === 11) {
    return "PF";
  }

  return "Nome Fantasia";
}

function obterDocumentoProprietario(proprietario) {
  const tipo = obterTipoProprietario(proprietario);

  if (tipo === "PJ" && (proprietario?.cnpj || proprietario?.documento)) {
    return mascararCNPJ(proprietario?.cnpj || proprietario?.documento || "");
  }

  if (tipo === "PF" && (proprietario?.cpf || proprietario?.documento)) {
    return mascararCPF(proprietario?.cpf || proprietario?.documento || "");
  }

  // Proprietário estrangeiro sem CPF/CNPJ.
  return descreverDocumentoEstrangeiro(proprietario);
}

function obterCnpjProprietario(proprietario) {
  if (obterTipoProprietario(proprietario) !== "PJ") {
    return "";
  }

  const cnpj = proprietario?.cnpj || proprietario?.documento || "";
  return cnpj ? mascararCNPJ(cnpj) : descreverDocumentoEstrangeiro(proprietario);
}

// ============================================================
// VIA CEP
// ============================================================

async function buscarEnderecoPorCep(cep) {
  try {
    const semMascara = apenasDigitos(cep);

    if (semMascara.length !== 8) {
      return null;
    }

    const res = await fetch(`https://viacep.com.br/ws/${semMascara}/json/`);

    const data = await res.json();

    if (!res.ok || data?.erro) {
      return null;
    }

    return {
      endereco: data.logradouro || "",
      bairro: data.bairro || "",
      cidade: data.localidade || "",
      uf: data.uf || "",
    };
  } catch (err) {
    console.error("Erro ao consultar CEP:", err);
    return null;
  }
}

// ============================================================
// MODAL DE PROPRIETÁRIOS
// ============================================================

function ModalSelecionarProprietario({ open, onClose, onSelect, proprietarios }) {
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(10);

  useEffect(() => {
    if (!open) {
      setFiltro("");
      setPagina(0);
    }
  }, [open]);

  const listaFiltrada = useMemo(() => {
    const termo = normalizarTexto(filtro);

    if (!termo) {
      return proprietarios;
    }

    return proprietarios.filter((proprietario) => {
      const documento = obterDocumentoProprietario(proprietario);
      const tipo = obterTipoProprietario(proprietario);

      const nome = proprietario?.nome || proprietario?.razaoSocial || "";

      return normalizarTexto(`${documento} ${tipo} ${nome}`).includes(termo);
    });
  }, [filtro, proprietarios]);

  const paginaAtual = listaFiltrada.slice(pagina * porPagina, pagina * porPagina + porPagina);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "var(--ig-f-2c2c2c)",
          color: "var(--ig-t-ffffff)",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          color: "var(--ig-t-ffffff)",
          fontWeight: 700,
        }}
      >
        Selecionar Proprietário
        <IconButton type="button" onClick={onClose}>
          <CloseIcon sx={{ color: "var(--ig-t-aaaaaa)" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2, pt: 1 }}>
          <TextField
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPagina(0);
            }}
            placeholder="Pesquisar por CPF, CNPJ, tipo ou nome."
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{
                        color: "var(--ig-t-9ca3af)",
                        fontSize: 20,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "var(--ig-f-2a2a2a)",
                color: "var(--ig-t-ffffff)",

                "& fieldset": {
                  borderColor: "var(--ig-b-444444)",
                },

                "&:hover fieldset": {
                  borderColor: "var(--ig-b-888888)",
                },
              },

              "& input": {
                color: "var(--ig-t-ffffff)",
              },
            }}
          />
        </Box>

        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "52%" }} />
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-252525)",
                  color: "var(--ig-t-aaaaaa)",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                CPF / CNPJ
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-252525)",
                  color: "var(--ig-t-aaaaaa)",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                Tipo
              </TableCell>

              <TableCell
                sx={{
                  backgroundColor: "var(--ig-f-252525)",
                  color: "var(--ig-t-aaaaaa)",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                Nome / Razão Social
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginaAtual.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  sx={{
                    color: "var(--ig-t-888888)",
                    py: 3,
                  }}
                >
                  Nenhum proprietário encontrado.
                </TableCell>
              </TableRow>
            ) : (
              paginaAtual.map((proprietario, index) => {
                const id = proprietario?.id ?? proprietario?._id ?? index;

                const documento = obterDocumentoProprietario(proprietario);

                const tipo = obterTipoProprietario(proprietario);

                const nome = proprietario?.nome || proprietario?.razaoSocial || "";

                return (
                  <TableRow
                    key={id}
                    hover
                    onClick={() => {
                      onSelect(proprietario);
                      onClose();
                    }}
                    sx={{
                      cursor: "pointer",

                      "&:hover": {
                        backgroundColor: "var(--ig-f-343434)",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: "var(--ig-t-cccccc)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {documento || "—"}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "var(--ig-t-cccccc)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tipo}
                    </TableCell>

                    <TableCell
                      sx={{
                        color: "var(--ig-t-ffffff)",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {String(nome).toUpperCase()}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={listaFiltrada.length}
          page={pagina}
          onPageChange={(_, novaPagina) => setPagina(novaPagina)}
          rowsPerPage={porPagina}
          onRowsPerPageChange={(e) => {
            setPorPagina(parseInt(e.target.value, 10));

            setPagina(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Proprietários por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{
            color: "var(--ig-t-ffffff)",

            "& .MuiTablePagination-selectIcon": {
              color: "var(--ig-t-ffffff)",
            },

            "& .MuiTablePagination-actions button": {
              color: "var(--ig-t-ffffff)",
              width: "auto",
            },
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
        }}
      >
        <Button
          type="button"
          variant="outlined"
          onClick={onClose}
          sx={{
            color: "var(--ig-t-ffffff)",
            borderColor: "#64748b",
            textTransform: "none",
          }}
        >
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================
// PÁGINA
// ============================================================

function CadastroTemplateCT() {
  // ============================================================
  // LISTAGEM
  // ============================================================

  const [templates, setTemplates] = useState([]);
  const [carregandoTemplates, setCarregandoTemplates] = useState(false);

  const [salvando, setSalvando] = useState(false);

  const [modoVisualizacao, setModoVisualizacao] = useState(false);

  const [modoEdicao, setModoEdicao] = useState(false);

  const [idVisualizando, setIdVisualizando] = useState(null);

  const [logoSalvaPreview, setLogoSalvaPreview] = useState("");

  const [logoSalvaNome, setLogoSalvaNome] = useState("");

  const [modalDocumentoAberto, setModalDocumentoAberto] = useState(false);

  const formularioRef = useRef(null);

  // ============================================================
  // PERMISSÕES
  // ============================================================

  const tokenUsuario = localStorage.getItem("token");

  let tipoUsuario = "";

  if (tokenUsuario) {
    const decoded = jwtDecode(tokenUsuario);

    tipoUsuario =
      decoded.tipo
        ?.toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") || "";
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);

  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  const formTravado = modoVisualizacao && !modoEdicao;

  // ============================================================
  // CARREGAR TEMPLATES CT
  // ============================================================

  const carregarTemplates = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    setCarregandoTemplates(true);

    try {
      const res = await fetch("/api/cadastro-template-ct", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.erro || `Erro ao carregar Templates CT. HTTP ${res.status}`);
      }

      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar Cadastro Template CT:", err);

      setTemplates([]);
    } finally {
      setCarregandoTemplates(false);
    }
  }, []);

  useEffect(() => {
    carregarTemplates();
  }, [carregarTemplates]);

  // ============================================================
  // PROPRIETÁRIOS
  // ============================================================

  const [proprietarios, setProprietarios] = useState([]);
  const [carregandoProprietarios, setCarregandoProprietarios] = useState(false);

  const [modalProprietarioAberto, setModalProprietarioAberto] = useState(false);

  // ============================================================
  // FORMULÁRIO
  // ============================================================

  const [form, setForm] = useState(FORM_VAZIO);

  const [logoArquivo, setLogoArquivo] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const inputLogoRef = useRef(null);

  const [consultandoCep, setConsultandoCep] = useState(false);
  const [erroCep, setErroCep] = useState("");

  // ============================================================
  // GRID
  // ============================================================

  const [filtro, setFiltro] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [templatesPorPagina, setTemplatesPorPagina] = useState(5);

  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("cadastroTemplateCTGridMinimizado");

    return saved ? saved === "1" : false;
  });

  // ============================================================
  // CARREGAR PROPRIETÁRIOS
  // ============================================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    setCarregandoProprietarios(true);

    fetch("/api/proprietarios", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((data) => {
        const lista = Array.isArray(data) ? data : Array.isArray(data?.proprietarios) ? data.proprietarios : [];

        lista.sort((a, b) => String(a?.nome || a?.razaoSocial || "").localeCompare(String(b?.nome || b?.razaoSocial || ""), "pt-BR"));

        setProprietarios(lista);
      })
      .catch((err) => {
        console.error("Erro ao carregar proprietários:", err);

        setProprietarios([]);
      })
      .finally(() => {
        setCarregandoProprietarios(false);
      });
  }, []);

  // ============================================================
  // MINIMIZAR
  // ============================================================

  useEffect(() => {
    localStorage.setItem("cadastroTemplateCTGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // ============================================================
  // PREVIEW LOGO
  // ============================================================

  useEffect(() => {
    if (!logoArquivo) {
      setLogoPreview("");
      return undefined;
    }

    const url = URL.createObjectURL(logoArquivo);

    setLogoPreview(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [logoArquivo]);

  // ============================================================
  // FILTRO
  // ============================================================

  const termos = filtro
    .split(",")
    .map((termo) => normalizarTexto(termo))
    .filter(Boolean);

  const filtrados = templates.filter((item) =>
    termos.every((termo) =>
      [
        item?.proprietarioNome,
        item?.cnpj,
        item?.cep,
        item?.endereco,
        item?.numero,
        item?.complemento,
        item?.bairro,
        item?.cidade,
        item?.uf,
        item?.email,
        item?.telefone,
      ]
        .join(" ")
        .toLowerCase()
        .includes(termo),
    ),
  );

  const totalRegistros = filtrados.length;

  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / templatesPorPagina));

  useEffect(() => {
    if (paginaAtual > totalPaginas) {
      setPaginaAtual(totalPaginas);
    }
  }, [paginaAtual, totalPaginas]);

  const indexInicial = (paginaAtual - 1) * templatesPorPagina;

  const indexFinal = Math.min(indexInicial + templatesPorPagina, totalRegistros);

  const registrosPaginados = filtrados.slice(indexInicial, indexFinal);

  // ============================================================
  // PROPRIETÁRIO
  // ============================================================

  const handleSelecionarProprietario = (proprietario) => {
    if (!proprietario) {
      return;
    }

    const proprietarioId = proprietario?.id ?? proprietario?._id ?? null;

    const proprietarioNome = proprietario?.nome || proprietario?.razaoSocial || "";

    const cnpj = obterCnpjProprietario(proprietario);

    setForm((prev) => ({
      ...prev,
      proprietarioId,

      proprietarioNome: String(proprietarioNome).toUpperCase(),

      cnpj,
    }));

    setModalProprietarioAberto(false);
  };

  // ============================================================
  // CEP
  // ============================================================

  const handleCepChange = async (event) => {
    const cepMascarado = mascararCEP(event.target.value);

    setForm((prev) => ({
      ...prev,
      cep: cepMascarado,
    }));

    setErroCep("");

    const digitos = apenasDigitos(cepMascarado);

    if (digitos.length !== 8) {
      return;
    }

    setConsultandoCep(true);

    const endereco = await buscarEnderecoPorCep(cepMascarado);

    setConsultandoCep(false);

    if (!endereco) {
      setErroCep("CEP não localizado.");
      return;
    }

    setForm((prev) => ({
      ...prev,

      cep: cepMascarado,

      endereco: endereco.endereco || prev.endereco,

      bairro: endereco.bairro || prev.bairro,

      cidade: endereco.cidade || prev.cidade,

      uf: endereco.uf || prev.uf,
    }));
  };

  // ============================================================
  // LOGO
  // ============================================================

  const abrirSeletorLogo = () => {
    inputLogoRef.current?.click();
  };

  const handleSelecionarLogo = (event) => {
    const arquivo = event.target.files?.[0];

    event.target.value = "";

    if (!arquivo) {
      return;
    }

    const tiposPermitidos = ["image/png", "image/jpeg", "image/webp"];

    if (!tiposPermitidos.includes(arquivo.type)) {
      alert("Selecione somente arquivos de imagem PNG, JPG, JPEG ou WEBP.");

      return;
    }

    setLogoArquivo(arquivo);
  };

  const handleRemoverLogo = () => {
    setLogoArquivo(null);
  };

  // ============================================================
  // LIMPAR FORM
  // ============================================================

  const limparFormulario = () => {
    setForm(FORM_VAZIO);

    setLogoArquivo(null);

    setLogoSalvaPreview("");
    setLogoSalvaNome("");

    setErroCep("");

    setModoVisualizacao(false);
    setModoEdicao(false);

    setIdVisualizando(null);

    setModalDocumentoAberto(false);
  };

  useEffect(() => {
    return () => {
      if (logoSalvaPreview) {
        URL.revokeObjectURL(logoSalvaPreview);
      }
    };
  }, [logoSalvaPreview]);

  // ============================================================
  // VISUALIZAR
  // ============================================================

  const handleVisualizar = async (item) => {
    if (!podeVisualizar) {
      alert("Você não tem permissão para visualizar.");

      return;
    }

    const id = item?.id ?? item?._id;

    if (!id) {
      alert("Registro inválido.");

      return;
    }

    setForm({
      proprietarioId: item?.proprietarioId ?? null,

      proprietarioNome: String(item?.proprietarioNome || "").toUpperCase(),

      cnpj: cnpjOuDocumentoEstrangeiro(item),

      cep: mascararCEP(item?.cep || ""),

      endereco: item?.endereco || "",

      numero: item?.numero || "",

      complemento: item?.complemento || "",

      bairro: item?.bairro || "",

      cidade: item?.cidade || "",

      uf: item?.uf || "",

      email: item?.email || "",

      telefone: mascararTelefone(item?.telefone || ""),
    });

    setLogoArquivo(null);

    setLogoSalvaPreview("");

    setLogoSalvaNome(item?.logo?.nomeOriginal || "");

    setErroCep("");

    setIdVisualizando(id);

    setModoVisualizacao(true);
    setModoEdicao(false);

    // ==========================================================
    // CARREGAR LOGO EXISTENTE
    // ==========================================================

    if (item?.logo) {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(`/api/cadastro-template-ct/${id}/logo`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const blob = await res.blob();

          const url = URL.createObjectURL(blob);

          setLogoSalvaPreview(url);
        }
      } catch (err) {
        console.error("Erro ao carregar Logo do Template CT:", err);
      }
    }

    requestAnimationFrame(() => {
      formularioRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  // ============================================================
  // VISUALIZAR COMO DOCUMENTO
  // ============================================================

  const handleVisualizarDocumento = () => {
    if (!idVisualizando) {
      alert("Nenhum Template CT selecionado.");

      return;
    }

    if (!logoSalvaPreview && !logoPreview) {
      alert("Este Template CT não possui uma Logo disponível para o documento.");

      return;
    }

    setModalDocumentoAberto(true);
  };

  // ============================================================
  // IMPRIMIR / SALVAR PDF
  // ============================================================

  const handleImprimirDocumento = () => {
    const logoDocumento = logoPreview || logoSalvaPreview;

    if (!logoDocumento) {
      alert("Não há Logo disponível para impressão.");

      return;
    }

    const rodape = montarRodapeDocumento(form);

    const janela = window.open("", "_blank", "width=950,height=900");

    if (!janela) {
      alert("O navegador bloqueou a janela de impressão. Libere os pop-ups e tente novamente.");

      return;
    }

    janela.document.open();

    janela.document.write(`
    <!DOCTYPE html>
    <html lang="pt-BR">
      <head>
        <meta charset="UTF-8" />

        <title>Template CT</title>

        <style>
          @page {
            size: A4 portrait;
            margin: 0;
          }

          * {
            box-sizing: border-box;
          }

          html,
          body {
            margin: 0;
            padding: 0;
            background: #ffffff;
          }

          body {
            font-family: Arial, Helvetica, sans-serif;
          }

          .pagina-a4 {
            width: 210mm;
            height: 297mm;
            position: relative;
            margin: 0 auto;
            background: #ffffff;
            overflow: hidden;
          }

          .logo-documento {
            position: absolute;
            top: 14mm;
            right: 18mm;

            max-width: 35mm;
            max-height: 18mm;

            width: auto;
            height: auto;

            object-fit: contain;
          }

          .rodape-documento {
            position: absolute;

            left: 18mm;
            right: 18mm;
            bottom: 12mm;

            color: #222222;

            font-size: 9pt;
            font-weight: 400;
            line-height: 1.45;

            text-align: center;

            overflow-wrap: anywhere;
          }

          @media print {
            html,
            body {
              width: 210mm;
              height: 297mm;
            }

            .pagina-a4 {
              margin: 0;
              box-shadow: none;
            }
          }
        </style>
      </head>

      <body>
        <div class="pagina-a4">
          <img
            src="${escaparHtml(logoDocumento)}"
            alt="Logo"
            class="logo-documento"
          />

          <div class="rodape-documento">
            ${escaparHtml(rodape)}
          </div>
        </div>

        <script>
          window.onload = function () {
            window.focus();

            setTimeout(function () {
              window.print();
            }, 250);
          };
        </script>
      </body>
    </html>
  `);

    janela.document.close();
  };

  // ============================================================
  // EDITAR
  // ============================================================

  const handleEditar = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!podeEditar) {
      alert("Você não tem permissão para editar.");

      return;
    }

    if (!idVisualizando) {
      alert("Nenhum Template CT selecionado.");

      return;
    }

    setModoEdicao(true);
  };

  // ============================================================
  // ATUALIZAR
  // ============================================================

  const handleAtualizar = async (e) => {
    e.preventDefault();

    if (salvando) {
      return;
    }

    if (!idVisualizando) {
      alert("Nenhum Template CT selecionado.");

      return;
    }

    if (!form.proprietarioId) {
      alert("Selecione um proprietário.");

      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      alert("Sessão não identificada. Faça login novamente.");

      return;
    }

    setSalvando(true);

    try {
      const payload = {
        proprietarioId: form.proprietarioId,

        cep: form.cep,

        endereco: form.endereco,

        numero: form.numero,

        complemento: form.complemento,

        bairro: form.bairro,

        cidade: form.cidade,

        uf: form.uf,

        email: form.email,

        telefone: form.telefone,
      };

      // ========================================================
      // 1. ATUALIZAR DADOS DO TEMPLATE CT
      // ========================================================

      const res = await fetch(`/api/cadastro-template-ct/${idVisualizando}`, {
        method: "PUT",

        headers: {
          "Content-Type": "application/json",

          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.erro || `Erro ao atualizar Template CT. HTTP ${res.status}`);
      }

      // ========================================================
      // 2. SUBSTITUIR LOGO, CASO UMA NOVA TENHA SIDO ESCOLHIDA
      // ========================================================

      if (logoArquivo) {
        const formData = new FormData();

        formData.append("logo", logoArquivo);

        const logoRes = await fetch(`/api/cadastro-template-ct/${idVisualizando}/logo`, {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,
          },

          body: formData,
        });

        const logoData = await logoRes.json().catch(() => null);

        if (!logoRes.ok) {
          throw new Error(
            logoData?.erro
              ? `Os dados foram atualizados, porém a Logo não foi atualizada: ${logoData.erro}`
              : "Os dados foram atualizados, porém não foi possível atualizar a Logo.",
          );
        }
      }

      // ========================================================
      // 3. RECARREGAR GRID
      // ========================================================

      await carregarTemplates();

      limparFormulario();

      setPaginaAtual(1);

      alert("Cadastro Template CT atualizado com sucesso!");
    } catch (err) {
      console.error("Erro ao atualizar Template CT:", err);

      alert(err?.message || "Erro ao atualizar Template CT.");
    } finally {
      setSalvando(false);
    }
  };

  // ============================================================
  // EXCLUIR
  // ============================================================

  const handleExcluir = async (item) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir.");

      return;
    }

    const id = item?.id ?? item?._id;

    if (!id) {
      alert("Registro inválido.");

      return;
    }

    const proprietario = item?.proprietarioNome || "";

    const confirmou = window.confirm(`Excluir o Cadastro Template CT de "${proprietario}"?`);

    if (!confirmou) {
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`/api/cadastro-template-ct/${id}`, {
        method: "DELETE",

        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.erro || "Erro ao excluir Cadastro Template CT.");
      }

      if (String(idVisualizando) === String(id)) {
        limparFormulario();
      }

      await carregarTemplates();

      alert("Cadastro Template CT excluído com sucesso!");
    } catch (err) {
      console.error("Erro ao excluir Cadastro Template CT:", err);

      alert(err?.message || "Erro ao excluir Cadastro Template CT.");
    }
  };

  // ============================================================
  // CADASTRAR
  // ============================================================

  const handleCadastrar = async (e) => {
    e.preventDefault();

    if (modoVisualizacao || modoEdicao) {
      return;
    }

    if (salvando) {
      return;
    }

    if (!form.proprietarioId) {
      alert("Selecione um proprietário.");

      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      alert("Sessão não identificada. Faça login novamente.");

      return;
    }

    setSalvando(true);

    try {
      const payload = {
        proprietarioId: form.proprietarioId,

        cep: form.cep,

        endereco: form.endereco,

        numero: form.numero,

        complemento: form.complemento,

        bairro: form.bairro,

        cidade: form.cidade,

        uf: form.uf,

        email: form.email,

        telefone: form.telefone,
      };

      // ========================================================
      // 1. CADASTRAR DADOS
      // ========================================================

      const res = await fetch("/api/cadastro-template-ct", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",

          Authorization: `Bearer ${token}`,
        },

        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.erro || `Erro ao cadastrar Template CT. HTTP ${res.status}`);
      }

      const templateId = data?.id;

      if (!templateId) {
        throw new Error("O cadastro foi realizado, mas o backend não retornou o ID do Template CT.");
      }

      // ========================================================
      // 2. ENVIAR LOGO, CASO TENHA SIDO SELECIONADA
      // ========================================================

      if (logoArquivo) {
        const formData = new FormData();

        /*
         * O backend foi configurado com:
         *
         * uploadLogo.single("logo")
         *
         * portanto o nome deve ser exatamente "logo".
         */
        formData.append("logo", logoArquivo);

        const logoRes = await fetch(`/api/cadastro-template-ct/${templateId}/logo`, {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,
          },

          body: formData,
        });

        const logoData = await logoRes.json().catch(() => null);

        if (!logoRes.ok) {
          throw new Error(
            logoData?.erro
              ? `O Template CT foi cadastrado, porém a Logo não foi gravada: ${logoData.erro}`
              : "O Template CT foi cadastrado, porém não foi possível gravar a Logo.",
          );
        }
      }

      // ========================================================
      // 3. ATUALIZAR GRID E LIMPAR FORMULÁRIO
      // ========================================================

      await carregarTemplates();

      limparFormulario();

      setPaginaAtual(1);

      alert("Cadastro Template CT realizado com sucesso!");
    } catch (err) {
      console.error("Erro ao cadastrar Template CT:", err);

      alert(err?.message || "Erro ao cadastrar Template CT.");
    } finally {
      setSalvando(false);
    }
  };

  // ============================================================
  // CSV
  // ============================================================

  const handleCSV = () => {
    if (filtrados.length === 0) {
      alert("Nada para exportar (lista vazia).");

      return;
    }

    const BOM = "\uFEFF";

    const header = ["Proprietário", "CNPJ", "CEP", "Endereço", "Número", "Complemento", "Bairro", "Cidade", "UF", "Email", "Telefone", "Logo"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = filtrados.map((item) => [
      item?.proprietarioNome,

      cnpjOuDocumentoEstrangeiro(item),

      item?.cep,
      item?.endereco,
      item?.numero,
      item?.complemento,
      item?.bairro,
      item?.cidade,
      item?.uf,
      item?.email,
      item?.telefone,
      item?.logoNome,
    ]);

    const csv = [header, ...linhas].map((linha) => linha.map(csvEscape).join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "cadastro-template-ct.csv");

    document.body.appendChild(link);

    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        {/* =====================================================
            MESMO PADRÃO INTERNO UTILIZADO EM PROPRIETÁRIOS
        ===================================================== */}

        <style>{`
          .campo-formulario-padrao,
          .campo-formulario-padrao:focus,
          .campo-formulario-padrao:active,
          .campo-formulario-padrao:read-only,
          .campo-formulario-padrao:disabled {
            background: transparent !important;
            background-color: transparent !important;
            color: var(--ig-t-ffffff) !important;
            box-shadow: none !important;
            -webkit-text-fill-color: var(--ig-t-ffffff) !important;
            opacity: 1 !important;
          }

          .campo-formulario-padrao::placeholder {
            color: var(--ig-t-255-255-255-a072) !important;
            -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
          }

          .campo-template-proprietario {
            position: relative;
            width: 100%;
          }

          .campo-template-proprietario input {
            padding-right: 46px !important;
            cursor: pointer;
          }

          .lupa-template-proprietario {
            position: absolute;
            right: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #f44336;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 2;
          }

          .campo-template-cnpj {
            cursor: default;
          }

          .linha-email-telefone {
            display: grid;
            grid-template-columns: 1.45fr 0.55fr;
            gap: 12px;
          }

          .linha-logo-template {
            display: flex;
            align-items: center;
            gap: 14px;
            min-height: 42px;
          }

          .btn-ged-template {
            width: auto !important;
            min-width: 86px !important;
            height: 40px;
            padding: 0 16px !important;
            background-color: #1998a8 !important;
            color: #ffffff !important;
            border: none !important;
            border-radius: 6px;
            font-weight: 700;
            cursor: pointer;
          }

          .btn-ged-template:hover {
            background-color: #177f8d !important;
          }

          .btn-remover-logo-template {
            width: auto !important;
            min-width: 100px !important;
            height: 40px;
            padding: 0 16px !important;
            background-color: transparent !important;
            color: #ff4c4c !important;
            border: 1px solid #d32f2f !important;
            border-radius: 6px;
            font-weight: 700;
            cursor: pointer;
          }

          .logo-preview-template {
            width: 130px;
            height: 80px;
            padding: 5px;
            object-fit: contain;
            box-sizing: border-box;
            background-color: #ffffff;
            border: 1px solid var(--ig-b-444444);
            border-radius: 5px;
          }

          .texto-arquivo-template {
            color: var(--ig-t-aaaaaa);
            font-size: 0.9rem;
          }

          .erro-cep-template {
            color: #ff4c4c;
            font-size: 0.8rem;
            margin-top: 4px;
          }

          .btn-documento-template {
  width: auto !important;
  min-width: 240px !important;
  height: 44px;

  padding: 0 18px !important;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  background-color: #1998a8 !important;
  color: #ffffff !important;

  border: none !important;
  border-radius: 6px;

  font-size: 15px;
  font-weight: 700;

  cursor: pointer;
}

.btn-documento-template:hover {
  background-color: #177f8d !important;
}

          @media (max-width: 800px) {
            .linha-email-telefone {
              grid-template-columns: 1fr;
            }

            .linha-logo-template {
              flex-wrap: wrap;
            }
          }
        `}</style>

        {/* =====================================================
            TÍTULO
        ===================================================== */}

        <h2>Cadastro Template CT</h2>

        {/* =====================================================
            FILTRO
        ===================================================== */}

        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Buscar Template CT"
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value);
                setPaginaAtual(1);
              }}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltro("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={async () => {
                const texto = filtrados
                  .map((item) => item.proprietarioNome)
                  .filter(Boolean)
                  .join(", ");

                if (!texto.trim()) {
                  alert("Nada para copiar (lista vazia).");

                  return;
                }

                const ok = await copiarTexto(texto);

                alert(ok ? "Copiado!" : "Não foi possível copiar.");
              }}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i
                className="fas fa-copy"
                style={{
                  marginRight: 6,
                }}
              />
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",

                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </Box>
        </Box>

        {/* =====================================================
            MINIMIZAR / EXPANDIR
        ===================================================== */}

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {/* =====================================================
            LISTA
        ===================================================== */}

        {!gridMinimizado && (
          <div className="bloco-lista">
            {carregandoTemplates ? (
              <p>Carregando Templates CT...</p>
            ) : filtrados.length === 0 ? (
              <p className="erro">Nenhum Template CT encontrado.</p>
            ) : (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>Proprietário</th>
                      <th>CNPJ</th>
                      <th>Cidade</th>
                      <th>UF</th>
                      <th>Telefone</th>
                      <th>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((item, index) => (
                      <tr key={item?.id ?? item?._id ?? index}>
                        <td>{item?.proprietarioNome || "—"}</td>

                        <td>{cnpjOuDocumentoEstrangeiro(item) || "—"}</td>

                        <td>{item?.cidade || "—"}</td>

                        <td>{item?.uf || "—"}</td>

                        <td>{item?.telefone || "—"}</td>

                        <td className="col-acoes">
                          {podeVisualizar && (
                            <button className="btn-editar" type="button" onClick={() => handleVisualizar(item)}>
                              <i className="fas fa-eye"></i>
                              Visualizar
                            </button>
                          )}

                          {podeExcluir && (
                            <button className="btn-excluir" type="button" onClick={() => handleExcluir(item)}>
                              <i className="fas fa-trash"></i>
                              Excluir
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <TablePagination
                  component="div"
                  count={filtrados.length}
                  page={paginaAtual - 1}
                  onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                  rowsPerPage={templatesPorPagina}
                  onRowsPerPageChange={(e) => {
                    setTemplatesPorPagina(parseInt(e.target.value, 10));

                    setPaginaAtual(1);
                  }}
                  rowsPerPageOptions={[5, 10, 25]}
                  labelRowsPerPage="Templates por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                  sx={{
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: "var(--ig-t-ffffff)",
                    borderTop: "1px solid var(--ig-b-444444)",

                    "& .MuiTablePagination-toolbar": {
                      minHeight: 42,
                    },

                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                      fontSize: "0.8rem",
                    },

                    "& .MuiTablePagination-selectIcon": {
                      color: "var(--ig-t-ffffff)",
                    },

                    "& .MuiTablePagination-actions button": {
                      color: "var(--ig-t-ffffff)",
                      width: "auto",
                    },

                    "& .MuiTablePagination-actions": {
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* =====================================================
            FORMULÁRIO
            MESMO PADRÃO DE PROPRIETÁRIOS
        ===================================================== */}

        <div className="bloco-formulario" ref={formularioRef}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            className="local-form"
          >
            {/* =================================================
    LINHA 1
    PROPRIETÁRIO + CNPJ
================================================= */}

            <div
              className="linha-dupla"
              style={{
                gap: 12,
                alignItems: "flex-end",
              }}
            >
              <div
                style={{
                  flex: "2 1 0",
                  minWidth: 280,
                }}
              >
                <label>Proprietário*</label>

                <div className="campo-template-proprietario">
                  <input
                    type="text"
                    value={form.proprietarioNome}
                    placeholder="Selecione o Proprietário"
                    readOnly
                    required
                    disabled={formTravado}
                    className="campo-formulario-padrao"
                    onClick={() => {
                      if (!formTravado) {
                        setModalProprietarioAberto(true);
                      }
                    }}
                  />

                  {!formTravado && (
                    <span className="lupa-template-proprietario" onClick={() => setModalProprietarioAberto(true)}>
                      {carregandoProprietarios ? (
                        <CircularProgress
                          size={16}
                          sx={{
                            color: "#f44336",
                          }}
                        />
                      ) : (
                        <SearchIcon
                          sx={{
                            fontSize: 20,
                          }}
                        />
                      )}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  flex: "1 1 0",
                  minWidth: 280,
                }}
              >
                <label>CNPJ</label>

                <input
                  type="text"
                  value={form.cnpj}
                  placeholder="00.000.000/0000-00"
                  readOnly
                  disabled={formTravado}
                  className="campo-formulario-padrao campo-template-cnpj"
                />
              </div>
            </div>

            {/* =================================================
    LINHA 2
    CEP + ENDEREÇO + NÚMERO
================================================= */}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "0.75fr 1.7fr 0.55fr",
                gap: 12,
              }}
            >
              <div>
                <label>CEP</label>

                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <input
                    type="text"
                    value={form.cep}
                    placeholder="Digite o CEP"
                    maxLength={9}
                    inputMode="numeric"
                    disabled={formTravado}
                    className="campo-formulario-padrao"
                    onChange={handleCepChange}
                    style={{
                      paddingRight: consultandoCep ? "42px" : undefined,
                    }}
                  />

                  {consultandoCep && (
                    <span
                      style={{
                        position: "absolute",
                        right: 14,
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                      }}
                    >
                      <CircularProgress
                        size={16}
                        sx={{
                          color: "#1998a8",
                        }}
                      />
                    </span>
                  )}
                </div>

                {erroCep && <div className="erro-cep-template">{erroCep}</div>}
              </div>

              <div>
                <label>Endereço</label>

                <input
                  type="text"
                  value={form.endereco}
                  disabled={formTravado}
                  className="campo-formulario-padrao"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      endereco: e.target.value.toUpperCase(),
                    }))
                  }
                />
              </div>

              <div>
                <label>N°</label>

                <input
                  type="text"
                  value={form.numero}
                  disabled={formTravado}
                  className="campo-formulario-padrao"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      numero: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            {/* =================================================
    LINHA 3
    COMPLEMENTO
================================================= */}

            <div>
              <label>Complemento</label>

              <input
                type="text"
                value={form.complemento}
                disabled={formTravado}
                className="campo-formulario-padrao"
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    complemento: e.target.value.toUpperCase(),
                  }))
                }
              />
            </div>

            {/* =================================================
    LINHA 4
    BAIRRO + CIDADE + UF
================================================= */}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.45fr 1.15fr 0.4fr",
                gap: 12,
              }}
            >
              <div>
                <label>Bairro</label>

                <input
                  type="text"
                  value={form.bairro}
                  disabled={formTravado}
                  className="campo-formulario-padrao"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      bairro: e.target.value.toUpperCase(),
                    }))
                  }
                />
              </div>

              <div>
                <label>Cidade</label>

                <input
                  type="text"
                  value={form.cidade}
                  disabled={formTravado}
                  className="campo-formulario-padrao"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      cidade: e.target.value.toUpperCase(),
                    }))
                  }
                />
              </div>

              <div>
                <label>UF</label>

                <input
                  type="text"
                  value={form.uf}
                  maxLength={2}
                  disabled={formTravado}
                  className="campo-formulario-padrao"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      uf: e.target.value
                        .replace(/[^a-zA-Z]/g, "")
                        .slice(0, 2)
                        .toUpperCase(),
                    }))
                  }
                />
              </div>
            </div>

            {/* =================================================
    LINHA 5
    EMAIL + TELEFONE
================================================= */}

            <div className="linha-email-telefone">
              <div>
                <label>Email</label>

                <input
                  type="email"
                  value={form.email}
                  disabled={formTravado}
                  className="campo-formulario-padrao"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                />
              </div>

              <div>
                <label>Telefone</label>

                <input
                  type="text"
                  value={form.telefone}
                  placeholder="(00) 00000-0000"
                  maxLength={15}
                  inputMode="numeric"
                  disabled={formTravado}
                  className="campo-formulario-padrao"
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      telefone: mascararTelefone(e.target.value),
                    }))
                  }
                />
              </div>
            </div>

            {/* =================================================
    LOGO
================================================= */}

            <div>
              <label>Logo</label>

              <input
                ref={inputLogoRef}
                type="file"
                accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                disabled={formTravado}
                onChange={handleSelecionarLogo}
                style={{
                  display: "none",
                }}
              />

              <div className="linha-logo-template">
                <button type="button" className="btn-ged-template" disabled={formTravado} onClick={abrirSeletorLogo}>
                  <i className="fas fa-file-upload" />
                  GED
                </button>

                <span className="texto-arquivo-template">{logoArquivo ? logoArquivo.name : logoSalvaNome || "Nenhuma imagem selecionada"}</span>

                {logoArquivo && !formTravado && (
                  <button type="button" className="btn-remover-logo-template" onClick={handleRemoverLogo}>
                    Remover
                  </button>
                )}

                {(logoPreview || logoSalvaPreview) && (
                  <img src={logoPreview || logoSalvaPreview} alt="Pré-visualização da Logo" className="logo-preview-template" />
                )}
              </div>

              <span
                style={{
                  display: "block",
                  marginTop: 6,
                  color: "var(--ig-t-888888)",
                  fontSize: "0.78rem",
                }}
              >
                Permitido somente imagem PNG, JPG, JPEG ou WEBP.
              </span>
            </div>

            {/* =================================================
    VISUALIZAR COMO DOCUMENTO
================================================= */}

            {modoVisualizacao && !modoEdicao && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 8,
                }}
              >
                <button type="button" className="btn-documento-template" onClick={handleVisualizarDocumento}>
                  <i className="fas fa-file-alt"></i>
                  Visualizar como Documento
                </button>
              </div>
            )}
            {/* =================================================
    AÇÕES
    PADRÃO VISUALIZAR / EDITAR / CADASTRAR
================================================= */}

            <div className="acoes-form-proprietarios">
              {!modoVisualizacao ? (
                // ========================================================
                // CADASTRO NOVO
                // ========================================================

                <button type="button" className="btn-cadastrar" disabled={salvando} onClick={handleCadastrar}>
                  <i className="fas fa-save"></i>

                  <span>{salvando ? "Cadastrando..." : "Cadastrar"}</span>
                </button>
              ) : !modoEdicao ? (
                // ========================================================
                // MODO VISUALIZAR
                // EDITAR | CANCELAR | EXCLUIR
                // ========================================================

                <>
                  {podeEditar && (
                    <button type="button" className="btn-cadastrar" onClick={handleEditar}>
                      <i className="fas fa-edit"></i>
                      Editar
                    </button>
                  )}

                  <button
                    type="button"
                    className="btn-cancelar"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      limparFormulario();
                    }}
                  >
                    <i className="fas fa-times"></i>
                    Cancelar
                  </button>

                  {podeExcluir && (
                    <button
                      type="button"
                      className="btn-excluir"
                      onClick={() =>
                        handleExcluir({
                          id: idVisualizando,
                          proprietarioNome: form.proprietarioNome,
                        })
                      }
                    >
                      <i className="fas fa-trash"></i>
                      Excluir
                    </button>
                  )}
                </>
              ) : (
                // ========================================================
                // MODO EDITAR
                // ATUALIZAR | CANCELAR | EXCLUIR
                // ========================================================

                <>
                  <button type="button" className="btn-cadastrar" disabled={salvando} onClick={handleAtualizar}>
                    <i className="fas fa-save"></i>

                    <span>{salvando ? "Atualizando..." : "Atualizar"}</span>
                  </button>

                  <button
                    type="button"
                    className="btn-cancelar"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      limparFormulario();
                    }}
                  >
                    <i className="fas fa-times"></i>
                    Cancelar
                  </button>

                  {podeExcluir && (
                    <button
                      type="button"
                      className="btn-excluir"
                      onClick={() =>
                        handleExcluir({
                          id: idVisualizando,
                          proprietarioNome: form.proprietarioNome,
                        })
                      }
                    >
                      <i className="fas fa-trash"></i>
                      Excluir
                    </button>
                  )}
                </>
              )}
            </div>
          </form>
        </div>

        {/* =====================================================
    MODAL VISUALIZAR COMO DOCUMENTO
===================================================== */}

        <Dialog
          open={modalDocumentoAberto}
          onClose={() => setModalDocumentoAberto(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",

              width: "min(96vw, 980px)",
              maxWidth: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",

              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            Visualizar como Documento
            <IconButton type="button" onClick={() => setModalDocumentoAberto(false)}>
              <CloseIcon
                sx={{
                  color: "var(--ig-t-aaaaaa)",
                }}
              />
            </IconButton>
          </DialogTitle>

          <DialogContent
            sx={{
              bgcolor: "var(--ig-f-3a3a3a)",
              p: 3,

              display: "flex",
              justifyContent: "center",

              overflow: "auto",
            }}
          >
            <div
              style={{
                width: "210mm",
                minWidth: "210mm",
                height: "297mm",

                position: "relative",

                backgroundColor: "#ffffff",

                boxShadow: "0 0 12px rgba(0,0,0,0.45)",

                overflow: "hidden",
              }}
            >
              {(logoPreview || logoSalvaPreview) && (
                <img
                  src={logoPreview || logoSalvaPreview}
                  alt="Logo do documento"
                  style={{
                    position: "absolute",

                    top: "14mm",
                    right: "18mm",

                    maxWidth: "35mm",
                    maxHeight: "18mm",

                    width: "auto",
                    height: "auto",

                    objectFit: "contain",
                  }}
                />
              )}

              <div
                style={{
                  position: "absolute",

                  left: "18mm",
                  right: "18mm",
                  bottom: "12mm",

                  color: "#222222",

                  fontFamily: "Arial, Helvetica, sans-serif",

                  fontSize: "9pt",
                  lineHeight: 1.45,

                  textAlign: "center",

                  overflowWrap: "anywhere",
                }}
              >
                {montarRodapeDocumento(form)}
              </div>
            </div>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,

              gap: 1,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => setModalDocumentoAberto(false)}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderColor: "#64748b",

                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Fechar
            </Button>

            <Button
              type="button"
              variant="contained"
              onClick={handleImprimirDocumento}
              sx={{
                backgroundColor: "#1998a8",

                color: "#ffffff",

                textTransform: "none",
                fontWeight: 700,

                "&:hover": {
                  backgroundColor: "#177f8d",
                },
              }}
            >
              <i
                className="fas fa-print"
                style={{
                  marginRight: 8,
                }}
              />
              Imprimir / Salvar PDF
            </Button>
          </DialogActions>
        </Dialog>

        {/* =====================================================
            MODAL PROPRIETÁRIO
        ===================================================== */}

        <ModalSelecionarProprietario
          open={modalProprietarioAberto}
          onClose={() => setModalProprietarioAberto(false)}
          proprietarios={proprietarios}
          onSelect={handleSelecionarProprietario}
        />
      </div>
    </ThemeProvider>
  );
}

export default CadastroTemplateCT;
